/**
 * Inspection Service — create, fetch, and compare check-in/check-out inspections.
 *
 * Handles:
 * - Image compression via `image-compress.ts` (same budget as listing photos)
 * - Supabase Storage upload to `inspections` bucket
 * - rental_inspections + rental_inspection_images table CRUD
 * - Rental status updates (checkin -> active, checkout -> completed)
 *
 * Class-based singleton per CLAUDE.md architecture rules.
 */
import { signedUrl } from '../utils/storage-url'
import * as Crypto from 'expo-crypto'
import { supabase } from '../supabase'
import { jsonObject } from '../types/db-rows'
import type { Row } from '../types/db-rows'
import { ServiceError } from '../errors/service-error'
import { captureException } from '../utils/sentry'
import { assertObjectStored, compressImage } from '../utils/image-compress'
import { uploadBytesWithSessionRecovery } from '../utils/storage-verify'
import { esElMismoInstante } from '../utils/dates'
import { inspectionFormSchema } from '../validations/inspection.schema'
import { CONDITION_GRADES, INSPECTION_ASSURANCE_STATUSES } from '../types/inspection.types'
import { advanceInspectionRentalStatus } from './inspection-status-transition'
import type {
  InspectionType,
  InspectionFormData,
  InspectionDetail,
  InspectionImageItem,
  InspectionAssuranceReview,
  InspectionAssuranceStatus,
  CheckoutComparison,
  ConditionGrade,
  InspectionPhoto,
} from '../types/inspection.types'

// ─── Constants ──────────────────────────────────────────────────────────────

const BUCKET = 'inspections'
/**
 * Las firmas van a SU PROPIO bucket, y no es una preferencia de orden.
 *
 * `inspections` declara `allowed_mime_types = {image/jpeg,image/png,image/webp,
 * image/heic,image/heif}` — verificado contra producción, no contra el schema.
 * Un SVG subido ahí lo rechaza Storage, el error se relanza, y como las firmas
 * suben ANTES que las fotos, el check-in entero moría con la fila de
 * `rental_inspections` ya insertada. Peor: desde ahí no había reintento, porque
 * la política `inspections_participant_insert` exige que la renta esté en
 * 'confirmed' o 'active' y el salto de estado ya la había movido.
 *
 * El bucket `signatures` existe desde el schema inicial, acepta
 * `image/svg+xml`, es privado y tiene tope de 1 MB — un trazo pesa kilobytes.
 * Estaba ahí todo el tiempo; el defecto fue no usarlo.
 */
const SIGNATURE_BUCKET = 'signatures'

// ─── Row Mappers ────────────────────────────────────────────────────────────

function mapInspectionRow(
  row: Row<'rental_inspections'>,
): Omit<InspectionDetail, 'images' | 'assuranceReview' | 'guestSignatureUrl' | 'hostSignatureUrl'> {
  return {
    id: row.id,
    rentalId: row.rental_id,
    inspectionType: fromDbInspectionType(row.inspection_type),
    performedBy: row.performed_by,
    fuelLevel: row.fuel_level,
    odometerKm: row.odometer_km,
    exteriorCondition: asConditionGrade(row.exterior_condition),
    interiorCondition: asConditionGrade(row.interior_condition),
    notes: row.notes,
    createdAt: row.created_at,
  }
}

function mapImageRow(row: Row<'rental_inspection_images'>): InspectionImageItem {
  return {
    id: row.id,
    inspectionId: row.inspection_id,
    url: row.url,
    area: row.area,
    storagePath: row.storage_path,
    isDamage: row.is_damage,
    caption: row.caption,
    captureSource: row.capture_source,
    capturedAt: row.captured_at,
    photoSha256: row.photo_sha256,
    sequenceIndex: row.sequence_index,
    latitude: row.latitude,
    longitude: row.longitude,
    locationAccuracyMeters: row.location_accuracy_meters,
    verificationPayload: jsonObject(row.verification_payload),
  }
}

function mapAssuranceReviewRow(
  row: Row<'inspection_assurance_reviews'>,
): InspectionAssuranceReview {
  return {
    id: row.id,
    inspectionId: row.inspection_id,
    rentalId: row.rental_id,
    inspectionType: fromDbInspectionType(row.inspection_type),
    partner: row.partner,
    partnerSubmissionId: row.partner_submission_id,
    status: asAssuranceStatus(row.status),
    submittedAt: row.submitted_at,
    dueAt: row.due_at,
    completedAt: row.completed_at,
    reportUrl: row.report_url,
    reportStoragePath: row.report_storage_path,
    resultSummary: row.result_summary,
    rejectionReasons: row.rejection_reasons ?? [],
    photoCount: row.photo_count,
    evidenceHash: row.evidence_hash,
    lastError: row.last_error,
    lastSyncedAt: row.last_synced_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

/**
 * `exterior_condition` / `interior_condition` / `status` are plain text
 * columns, so the generated types cannot narrow them. Validate against the
 * domain union instead of asserting it.
 */
function asConditionGrade(value: string | null): ConditionGrade | null {
  return CONDITION_GRADES.find((grade) => grade === value) ?? null
}

function asAssuranceStatus(value: string): InspectionAssuranceStatus {
  return INSPECTION_ASSURANCE_STATUSES.find((status) => status === value) ?? 'pending_submission'
}

function toDbInspectionType(type: InspectionType): 'pickup' | 'dropoff' {
  return type === 'checkin' ? 'pickup' : 'dropoff'
}

function fromDbInspectionType(type: string): InspectionType {
  return type === 'dropoff' || type === 'checkout' ? 'checkout' : 'checkin'
}

function bytesToHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

// ─── Service ────────────────────────────────────────────────────────────────

class InspectionServiceImpl {
  /**
   * Create an inspection (check-in or check-out).
   *
   * 1. Validate form data with Zod schema
   * 2. Insert rental_inspections row
   * 3. Upload photos sequentially (memory-safe) to Storage
   * 4. Insert rental_inspection_images rows
   * 5. Update rental status
   */
  async createInspection(
    rentalId: string,
    userId: string,
    type: InspectionType,
    formData: InspectionFormData,
    onPhotoProgress?: (completed: number, total: number) => void,
  ): Promise<InspectionDetail> {
    // 1. Validate
    const parsed = inspectionFormSchema.safeParse(formData)
    if (!parsed.success) {
      const firstError = parsed.error.issues[0]?.message ?? 'Invalid form data'
      throw ServiceError.validation(firstError, {
        errors: parsed.error.issues.map((e) => e.message),
      })
    }

    /*
     * 2. La fila de la inspeccion — UPSERT, no INSERT.
     *
     * Reintentar es el camino normal aca, no el raro: si fallan fotos, la
     * pantalla deja volver a apretar. Con `insert` cada intento escribia OTRA
     * fila, y como `getInspection` lee con `.maybeSingle()`, dos filas no
     * devuelven la primera: TIRAN ERROR. O sea el segundo intento dejaba la
     * entrega ilegible para siempre, y con ella la devolucion.
     *
     * Ahora choca contra `rental_inspections_una_por_renta_y_tipo` y reusa la
     * fila. La invariante --una entrega y una devolucion por renta-- vive en la
     * base, no en la esperanza de que nadie toque dos veces.
     */
    const { data: inspectionRow, error: insertError } = await supabase
      .from('rental_inspections')
      .upsert(
        {
          rental_id: rentalId,
          performed_by: userId,
          inspection_type: toDbInspectionType(type),
          fuel_level: formData.fuelLevel,
          odometer_km: formData.odometerKm,
          exterior_condition: formData.exteriorCondition,
          interior_condition: formData.interiorCondition,
          notes: formData.notes || null,
        },
        { onConflict: 'rental_id,inspection_type' },
      )
      .select()
      .single()

    if (insertError) throw ServiceError.fromSupabaseError(insertError)

    const inspectionId = inspectionRow.id as string

    /*
     * 2b. Las firmas, antes que las fotos.
     *
     * Son dos ficheros de unos kilobytes contra diecisiete imágenes de varios
     * megas. Si una firma no se puede guardar, es mejor saberlo en el primer
     * segundo que después de subir toda la evidencia — que es exactamente el
     * error que tenía el paso de estado antes de hoy: fallar al final, cuando el
     * usuario ya hizo todo el trabajo.
     *
     * Una entrega sin firmas se sigue aceptando en esta capa. La pantalla es la
     * que las exige; acá permitirlas ausentes es lo que deja que un reintento de
     * subida de fotos no muera por un campo que no cambió.
     */
    const signatureUrls = await this.uploadSignatures(rentalId, inspectionId, formData)
    if (signatureUrls.guest || signatureUrls.host) {
      const { error: signatureError } = await supabase
        .from('rental_inspections')
        .update({
          ...(signatureUrls.guest ? { guest_signature_url: signatureUrls.guest } : {}),
          ...(signatureUrls.host ? { host_signature_url: signatureUrls.host } : {}),
        })
        .eq('id', inspectionId)

      if (signatureError) {
        captureException(signatureError, {
          scope: 'inspection-signature-save',
          rentalId,
          inspectionId,
        })
        throw ServiceError.fromSupabaseError(signatureError)
      }
    }

    /*
     * 3. Las fotos — subida idempotente por area, y por eso el reintento es barato.
     *
     * ANTES: el bucle recorria las 16 fotos completas en CADA intento, cada una
     * a una ruta con UUID nuevo, insertando una fila nueva. Y reintentar no es
     * el camino raro aca --este mismo metodo lanza `INSPECTION_PHOTOS_FAILED`
     * justo para que la pantalla deje volver a apretar--, asi que UNA foto
     * perdida en la calle duplicaba las otras quince. Dos juegos de fotos en la
     * misma inspeccion no se ven como un error: se ven como una entrega
     * exitosa, y la devolucion termina comparando contra ruido.
     *
     * AHORA hay dos mitades y las dos hacen falta:
     *   - La ruta y la fila son deterministicas por area (ver `uploadPhoto`),
     *     con el indice unico `rental_inspection_images_una_por_area` detras. Un
     *     reintento sobrescribe en vez de duplicar, aunque el cliente se
     *     equivoque.
     *   - Lo que ya esta arriba NO se vuelve a subir. `capturedAt` es la
     *     identidad de una toma: si coincide con la fila que ya existe, es la
     *     misma foto y se reusa; si la persona repitio la foto, cambia, y
     *     entonces si se sube. Con datos moviles salvadorenos, reintentar una
     *     entrega por una sola foto perdida no puede costar quince fotos de
     *     nuevo.
     */
    const { data: filasPrevias, error: previasError } = await supabase
      .from('rental_inspection_images')
      .select('*')
      .eq('inspection_id', inspectionId)

    if (previasError) throw ServiceError.fromSupabaseError(previasError)

    const previaPorArea = new Map((filasPrevias ?? []).map((row) => [row.area, row]))

    const uploadedImages: InspectionImageItem[] = []
    const failedAreas: string[] = []
    /*
     * Cuenta SOLO las que quedaron arriba, no las que ya se intentaron.
     *
     * El progreso avanzaba en las dos ramas del try, asi que quince fallos
     * pintaban la barra llena y la pantalla decia 15/15 un instante antes de
     * mostrar el error. Un progreso que cuenta fallos como avances es una
     * mentira que ademas borra la pista de cuantas faltan.
     */
    let subidas = 0

    for (let i = 0; i < formData.photos.length; i++) {
      const photo = formData.photos[i]!
      const previa = previaPorArea.get(photo.area)

      if (previa && esElMismoInstante(previa.captured_at, photo.capturedAt)) {
        uploadedImages.push(mapImageRow(previa))
        subidas += 1
        onPhotoProgress?.(subidas, formData.photos.length)
        continue
      }

      try {
        const image = await this.uploadPhoto(rentalId, inspectionId, photo, i + 1, Boolean(previa))
        uploadedImages.push(image)
        subidas += 1
      } catch (err) {
        failedAreas.push(photo.area)
        captureException(err, {
          scope: 'inspection-photo-upload',
          rentalId,
          inspectionId,
          area: photo.area,
          inspectionType: type,
        })
      }
      onPhotoProgress?.(subidas, formData.photos.length)
    }

    if (failedAreas.length > 0) {
      // Don't transition the rental — the caller handles the "retry failed
      // photos" modal and re-invokes submitInspection after the retake.
      throw new ServiceError({
        code: 'INSPECTION_PHOTOS_FAILED',
        message: `Failed to upload ${failedAreas.length} inspection photo(s)`,
        statusCode: 502,
        details: { failedAreas, inspectionId, rentalId },
      })
    }

    /*
     * 4. Walk the rental to its next state — one legal step at a time.
     *
     * This used to jump straight to the destination: 'confirmed' -> 'active' on
     * check-in, 'active' -> 'completed' on check-out. Neither of those is a
     * transition the database allows. `validate_rental_status_transition`
     * (20260422000006_rental_state_machine.sql:36-44) permits only:
     *
     *   confirmed      -> pending_pickup | cancelled | disputed
     *   pending_pickup -> active         | cancelled | disputed
     *   active         -> pending_return | disputed
     *   pending_return -> completed      | disputed
     *
     * So 'active' is not reachable from 'confirmed' and 'completed' is not
     * reachable from 'active'. The old code could only ever succeed on a rental
     * that was ALREADY sitting in pending_pickup or pending_return — and
     * nothing in the product ever put one there. Check-in and check-out could
     * not complete a trip, at all, ever. The photos uploaded, the inspection row
     * was written, the assurance review was submitted, and then the last line
     * raised and the guest saw a failure after doing all the work.
     *
     * Each hop carries its own prior-status guard, so this is safe to retry: a
     * run that dies between the two hops leaves the rental in the intermediate
     * state, and the next attempt picks it up from there rather than starting
     * over or double-advancing.
     */
    await advanceInspectionRentalStatus({
      rentalId,
      inspectionId,
      type,
      gateway: {
        updateIfCurrent: async ({ from, to }) => {
          const { data: moved, error: statusError } = await supabase
            .from('rentals')
            .update({ status: to })
            .eq('id', rentalId)
            .in('status', [...from])
            .select('id,status')

          if (statusError) {
            captureException(statusError, {
              scope: 'inspection-status-update',
              rentalId,
              inspectionId,
              inspectionType: type,
              targetStatus: to,
            })
            throw ServiceError.fromSupabaseError(statusError)
          }

          return (moved ?? []).length > 0
        },
        readCurrent: async () => {
          const { data: current, error: readError } = await supabase
            .from('rentals')
            .select('status')
            .eq('id', rentalId)
            .maybeSingle()

          if (readError) {
            captureException(readError, {
              scope: 'inspection-status-read',
              rentalId,
              inspectionId,
              inspectionType: type,
            })
            throw ServiceError.fromSupabaseError(readError)
          }

          return current?.status ?? null
        },
      },
      onUnexpectedStatus: (error, hop) => {
        captureException(error, {
          scope: 'inspection-status-unchanged',
          rentalId,
          inspectionId,
          inspectionType: type,
          targetStatus: hop.to,
          actualStatus: error.details?.actual,
        })
      },
    })

    /*
     * 5. Avisarle a la aseguradora — DESPUES de entregar el carro, y sin poder
     *    impedirlo.
     *
     * Esto corria ANTES de los saltos de estado y lanzaba con cualquier error,
     * asi que un correo interno que no salia dejaba la renta en 'confirmed'.
     * Traducido a la calle: el anfitrion y el huesped parados junto al carro,
     * las 15 fotos ya subidas, $57.50 ya cobrados, y el telefono diciendo que
     * no se pudo completar la entrega -- porque un correo que Tago se manda A
     * SI MISMA no salio.
     *
     * Y hoy esa es la UNICA rama posible: `INSPECTION_PARTNER_SUBMIT_URL` no
     * esta puesta y `partner_reviewers` no tiene ni una fila de qualitas, asi
     * que la funcion cae en `sendInternalReviewEmail` a team@tago.rent y
     * responde 502 si Resend no contesta 2xx. Su limite es de 6 envios por hora
     * y el 429 llega igual como 502. Cualquiera de esas cosas paraba una
     * entrega fisica.
     *
     * El orden correcto es este: el carro se entrega, y el aviso a la
     * aseguradora es una consecuencia, no un permiso. La fila del review queda
     * persistida por el propio edge function con su status y su last_error, asi
     * que hay de donde reintentar sin repetir la inspeccion.
     */
    let assuranceReview: InspectionAssuranceReview | null = null
    try {
      assuranceReview = await this.submitAssuranceReview(inspectionId)
    } catch (assuranceError) {
      captureException(assuranceError, {
        scope: 'inspection-assurance-submit',
        rentalId,
        inspectionId,
        inspectionType: type,
      })
    }

    // 6. Return the full inspection detail
    return {
      ...mapInspectionRow(inspectionRow),
      // Recién subidas: se devuelven firmadas para que quien acaba de terminar
      // la entrega pueda verlas en el acto, sin recargar.
      /*
       * FALLA ABIERTO, igual que el aviso a la aseguradora unas líneas arriba.
       *
       * Acá la renta YA avanzó y toda la evidencia ya está en la base: el
       * traspaso ocurrió. Si firmar una URL de presentación lanza --una red que
       * se cae justo acá-- el método entero tiraba error, la pantalla decía "No
       * se pudo completar la entrega", NO se borraba el borrador, y el
       * reintento chocaba contra una renta que ya había avanzado. La app
       * contradiciéndose sobre un hecho físico que sí pasó.
       *
       * Ninguna URL para mostrar una miniatura puede decidir eso.
       */
      guestSignatureUrl: await this.firmarOSilencio(signatureUrls.guest),
      hostSignatureUrl: await this.firmarOSilencio(signatureUrls.host),
      images: uploadedImages,
      assuranceReview,
    }
  }

  /**
   * Guardar las firmas como SVG.
   *
   * SVG y no PNG porque un trazo ES una lista de puntos: pesa kilobytes, se
   * amplía sin pixelarse a tamaño carta dentro de un PDF, y sigue siendo el
   * mismo dato dentro de diez años. Rasterizarlo sería tirar información para
   * ocupar más espacio.
   *
   * Van al bucket `signatures` — ver la nota en SIGNATURE_BUCKET — bajo la
   * misma ruta `rentalId/inspectionId/` que las fotos, para que la evidencia de
   * una entrega siga estando junta aunque viva en dos buckets.
   */
  private async uploadSignatures(
    rentalId: string,
    inspectionId: string,
    formData: InspectionFormData,
  ): Promise<{ guest: string | null; host: string | null }> {
    const upload = async (svg: string | null | undefined, who: 'guest' | 'host') => {
      const trimmed = (svg ?? '').trim()
      if (!trimmed) return null

      const storagePath = `${rentalId}/${inspectionId}/signature-${who}.svg`
      const { error } = await uploadBytesWithSessionRecovery(
        SIGNATURE_BUCKET,
        storagePath,
        new TextEncoder().encode(trimmed),
        {
          contentType: 'image/svg+xml',
          upsert: true,
        },
      )
      if (error) throw ServiceError.fromSupabaseError(error)
      return storagePath
    }

    const [guest, host] = await Promise.all([
      upload(formData.guestSignatureSvg, 'guest'),
      upload(formData.hostSignatureSvg, 'host'),
    ])
    return { guest, host }
  }

  /**
   * Sube UNA foto de inspeccion, de forma idempotente por area.
   *
   * @param sustituye true si ya existia una fila para esta area. Solo cambia
   * que hacer si la escritura en la base falla: cuando se esta REEMPLAZANDO una
   * foto, borrar el archivo dejaria a la fila que ya existe apuntando a la
   * nada. Se prefiere un byte de mas antes que evidencia rota.
   */
  private async uploadPhoto(
    rentalId: string,
    inspectionId: string,
    photo: InspectionPhoto,
    sequenceIndex: number,
    sustituye: boolean,
  ): Promise<InspectionImageItem> {
    const compressed = await compressImage(photo.uri, 'inspection')
    const bytes = compressed.bytes

    // SHA-256 for audit — computed DEFENSIVELY. expo-crypto's native digest on
    // Android (SDK 54) wants a TypedArray, per its own docs; a raw ArrayBuffer
    // threw on the native module (the same defect that broke vehicle photo
    // uploads). The hash is metadata, never a gate, so a failure must not block
    // the inspection upload.
    let photoSha256: string | null = null
    try {
      const digest = await Crypto.digest(Crypto.CryptoDigestAlgorithm.SHA256, new Uint8Array(bytes))
      photoSha256 = bytesToHex(digest)
    } catch {
      photoSha256 = null
    }

    /*
     * Ruta DETERMINISTICA por area, no un UUID nuevo cada vez.
     *
     * Con UUID, reintentar dejaba el archivo viejo huerfano en el bucket y una
     * fila nueva apuntando al nuevo: dos fotos del mismo angulo, sin forma de
     * saber cual vale. Con el area como nombre, subir la misma foto dos veces
     * escribe el mismo lugar dos veces. Eso es lo que hace que el reintento sea
     * seguro por construccion en vez de por suerte.
     */
    const storagePath = `${rentalId}/${inspectionId}/${photo.area}.jpg`

    const { error: uploadError } = await uploadBytesWithSessionRecovery(
      BUCKET,
      storagePath,
      bytes,
      {
        contentType: 'image/jpeg',
        upsert: true,
      },
    )

    if (uploadError) {
      throw new ServiceError({
        message: `Inspection image upload failed: ${uploadError.message}`,
        code: 'UPLOAD_FAILED',
        statusCode: 500,
        cause: uploadError,
      })
    }

    await assertObjectStored(BUCKET, storagePath)

    // `inspections` is a private bucket: getPublicUrl would return a URL that
    // always 400s, so the row must carry a signed URL.
    const publicUrl = await signedUrl(BUCKET, storagePath)

    // La fila, contra el indice unico (inspection_id, area).
    const { data, error: dbError } = await supabase
      .from('rental_inspection_images')
      .upsert(
        {
          inspection_id: inspectionId,
          storage_path: storagePath,
          url: publicUrl,
          area: photo.area,
          // Antes fijo en false, lo que dejaba la columna muerta: ninguna
          // inspección podía señalar un daño aunque la foto lo mostrara.
          is_damage: photo.isDamage === true,
          capture_source: photo.source,
          captured_at: photo.capturedAt,
          photo_sha256: photoSha256,
          sequence_index: sequenceIndex,
          latitude: photo.latitude ?? null,
          longitude: photo.longitude ?? null,
          location_accuracy_meters: photo.locationAccuracyMeters ?? null,
          verification_payload: {
            source: photo.source,
            originalWidth: photo.width,
            originalHeight: photo.height,
            compressedWidth: compressed.width,
            compressedHeight: compressed.height,
            // EXIF leído de la propia captura: ata el archivo al momento y al
            // lugar. Opcional: si la cámara no lo entregó, el rastro restante
            // (captured_at + sha256 + source + GPS) sigue siendo la evidencia.
            ...(photo.exif && Object.keys(photo.exif).length > 0 ? { exif: photo.exif } : {}),
          },
        },
        { onConflict: 'inspection_id,area' },
      )
      .select()
      .single()

    if (dbError) {
      // Solo se limpia lo que nadie estaba usando: si esta foto REEMPLAZA a una
      // que ya tenia fila, borrar el archivo dejaria esa fila apuntando a la
      // nada. Un archivo de sobra es basura; una fila rota es evidencia perdida.
      if (!sustituye) {
        await supabase.storage.from(BUCKET).remove([storagePath])
      }
      throw ServiceError.fromSupabaseError(dbError)
    }

    return mapImageRow(data)
  }

  /** URL firmada de una firma, o null si no se puede. Nunca lanza. */
  private async firmarOSilencio(ruta: string | null): Promise<string | null> {
    if (!ruta) return null
    try {
      return await signedUrl(SIGNATURE_BUCKET, ruta)
    } catch {
      return null
    }
  }

  private async submitAssuranceReview(
    inspectionId: string,
  ): Promise<InspectionAssuranceReview | null> {
    // The edge function echoes back the row it inserted, so declare that
    // contract on `invoke` rather than casting the response afterwards.
    const { data, error } = await supabase.functions.invoke<{
      review?: Row<'inspection_assurance_reviews'> | null
    }>('submit-inspection-assurance', {
      body: { inspectionId },
    })

    if (error) {
      // Preserve the Edge Function's actual HTTP status.  Replacing every
      // response with 502 made expected 4xx validation/auth failures look like
      // infrastructure incidents in Sentry and hid the real action from the
      // caller.  The shared mapper also keeps 5xx failures visible.
      throw ServiceError.fromSupabaseError(error)
    }

    return data?.review ? mapAssuranceReviewRow(data.review) : null
  }

  /**
   * Get an inspection by rental ID and type.
   * Returns null if not found.
   */
  async getInspection(rentalId: string, type: InspectionType): Promise<InspectionDetail | null> {
    const { data: row, error } = await supabase
      .from('rental_inspections')
      .select('*')
      .eq('rental_id', rentalId)
      .eq('inspection_type', toDbInspectionType(type))
      .maybeSingle()

    if (error) throw ServiceError.fromSupabaseError(error)
    if (!row) return null

    // Fetch images
    const { data: imageRows, error: imgError } = await supabase
      .from('rental_inspection_images')
      .select('*')
      .eq('inspection_id', row.id)
      .order('created_at', { ascending: true })

    if (imgError) throw ServiceError.fromSupabaseError(imgError)

    const { data: reviewRow, error: reviewError } = await supabase
      .from('inspection_assurance_reviews')
      .select('*')
      .eq('inspection_id', row.id)
      .maybeSingle()

    if (reviewError) throw ServiceError.fromSupabaseError(reviewError)

    /*
     * El bucket `signatures` es privado, así que la columna guarda una RUTA, no
     * una URL. Sin firmarla no se puede pintar en ningún lado — que es
     * exactamente por qué las firmas llevaban desde siempre sin verse.
     *
     * Falla abierto: si no se puede firmar la URL, la inspección se devuelve
     * igual y la pantalla dice "firmada" sin miniatura. Una firma que no se
     * pinta no puede tumbar la ficha del viaje entera.
     */
    return {
      ...mapInspectionRow(row),
      guestSignatureUrl: await this.firmarOSilencio(row.guest_signature_url),
      hostSignatureUrl: await this.firmarOSilencio(row.host_signature_url),
      images: (imageRows ?? []).map(mapImageRow),
      assuranceReview: reviewRow ? mapAssuranceReviewRow(reviewRow) : null,
    }
  }

  /**
   * Fetch both check-in and check-out inspections, compute comparison metrics.
   */
  async getCheckoutComparison(rentalId: string): Promise<CheckoutComparison | null> {
    const [checkin, checkout] = await Promise.all([
      this.getInspection(rentalId, 'checkin'),
      this.getInspection(rentalId, 'checkout'),
    ])

    if (!checkin || !checkout) return null

    return {
      checkin,
      checkout,
      fuelDifference: checkout.fuelLevel - checkin.fuelLevel,
      kmDriven: checkout.odometerKm - checkin.odometerKm,
    }
  }
}

// ─── Singleton Export ───────────────────────────────────────────────────────

export const inspectionService = new InspectionServiceImpl()
